/**
 * Event handlers for network activity particles
 */

import { bus } from '../../../core/events.js';

export interface NetworkActivityState {
  userSpeaking: boolean;
  aiResponding: boolean;
  userSpeechTimeout: ReturnType<typeof setTimeout> | null;
  aiResponseTimeout: ReturnType<typeof setTimeout> | null;
}

/**
 * Initialize event handlers for network activity particles
 */
export function setupNetworkEventHandlers(
  state: NetworkActivityState,
  onCreateConnection: (type: 'microphone' | 'llm', isStartup?: boolean) => void
): void {
  // Listen for user speech events - create white line directly (event-driven)
  bus.on('user.voice', (payload: any) => {
    state.userSpeaking = true;
    state.aiResponding = false;

    // Check if this is a startup event (privacy activation)
    const isStartup = payload?._test === true || payload?.transcript?.includes('privacy activation');

    // Create white line directly from event (microphone path on main pipe)
    onCreateConnection('microphone', isStartup);

    // Clear existing timeout
    if (state.userSpeechTimeout) {
      clearTimeout(state.userSpeechTimeout);
    }

    // Reset after 2 seconds (user finished speaking)
    state.userSpeechTimeout = setTimeout(() => {
      state.userSpeaking = false;
    }, 2000);
  });

  // Listen for AI drawing activity
  bus.on('drawing.aiStrokeAdded', () => {
    state.aiResponding = true;
    state.userSpeaking = false;

    // Clear existing timeout
    if (state.aiResponseTimeout) {
      clearTimeout(state.aiResponseTimeout);
    }

    // Reset after 3 seconds (AI finished responding)
    state.aiResponseTimeout = setTimeout(() => {
      state.aiResponding = false;
    }, 3000);
  });

  bus.on('drawing.aiCommandExecuted', (payload: any) => {
    state.aiResponding = true;
    state.userSpeaking = false;

    // Check if this is a startup event (privacy activation)
    const isStartup = payload?._test === true || payload?.command?.id?.includes('privacy-activation');

    // Create white line directly from event (LLM prompt path on main pipe)
    onCreateConnection('llm', isStartup);

    // Clear existing timeout
    if (state.aiResponseTimeout) {
      clearTimeout(state.aiResponseTimeout);
    }

    // Reset after 3 seconds (AI finished responding)
    state.aiResponseTimeout = setTimeout(() => {
      state.aiResponding = false;
    }, 3000);
  });
}

/**
 * Cleanup event handlers
 */
export function cleanupNetworkEventHandlers(state: NetworkActivityState): void {
  if (state.userSpeechTimeout) {
    clearTimeout(state.userSpeechTimeout);
    state.userSpeechTimeout = null;
  }
  if (state.aiResponseTimeout) {
    clearTimeout(state.aiResponseTimeout);
    state.aiResponseTimeout = null;
  }
}
